<template>
  <LoadingComponent v-if="loading" size="small" />
  <template v-if="!loading && localDashboard">
    <div class="shrink-0">
      <DashboardHeader :dashboard="localDashboard">
        <template #cta>
          <DashboardPanelControl
            ref="panel_control"
            v-model:search-value="searchValue"
            v-model:dashboard="localDashboard"
            v-model:require-save-dashboard="requireSaveDashboard"
            :count-filter="validFilter.length"
            :is-dirty="$isStateDirty('localDashboard.filters')"
            :need-save-locked="loading"
            :selection-active="selectedItems.length > 0"
            @dashboard-changed="dashboardChanged"
            @export-file="handleExportFile"
            @clear-filters="clearFilters"
          >
            <template #cta>
              <BaseButton
                v-if="selectedItems.length > 0 && !paymentForm"
                color="warning"
                icon="euro-sign"
                :title="$t('global.add_settlement')"
                size="small"
                @click="openNewPaymentForm"
              />
            </template>
            <template #filter-content="{ close }">
              <FilterLinesComponent
                v-model:filters="localDashboard.filters"
                v-model:require-save-dashboard="requireSaveDashboard"
                :filters-available="availableFilter"
                :dashboard-type="localDashboard.type"
                @close-popover="() => closePopover(close)"
              />
            </template>
            <template #other-cta>
              <AddNewColumnComponent
                model="third_party_cost"
                @add-column="addColumn"
              />
            </template>
          </DashboardPanelControl>
        </template>
      </DashboardHeader>
    </div>
    <div class="relative flex min-h-0 flex-1 flex-col gap-4 overflow-auto">
      <AlertPanel
        v-if="limitAlert"
        type="warning"
        compact
        :title="$t(`alert.${limitAlert.code}`, { limit: limitAlert.limit })"
        :actions="[
          {
            id: 'force-unlimited',
            name: $t('button.display_all_results'),
            action: overrideLimit
          }
        ]"
      />
      <div
        v-if="loadingItems"
        class="absolute top-1/2 left-1/2 z-100 h-36 w-36 -translate-x-1/2 -translate-y-1/2 transform rounded-2xl bg-gray-200"
      >
        <LoadingComponent :display-label="false" />
      </div>
      <ThirdPartyCostTable
        v-model:columns="localDashboard.columns"
        v-model:sort="localDashboard.sort"
        v-model:require-save-dashboard="requireSaveDashboard"
        :items="filteredItems"
        :loading="loadingItems"
        :selected-items="selectedItems"
        @select-item="toggleSelection"
        @print-item="printPaymentProof"
        @edit-item="openEditDialog"
        @update:dashboard="updateDashboard"
      />
    </div>
  </template>

  <InvoiceItemEditDialog
    v-if="currentItem && showEditDialog"
    v-model:is-modal-open="showEditDialog"
    :footer="true"
    :invoice-item="currentItem"
    :is-for-invoice="false"
    @refresh-list="fetchItems"
    @open-payment="openEditPaymentForm"
  />

  <ThirdPartyCostPaymentFormModal
    v-if="paymentForm"
    v-model:is-modal-open="paymentForm"
    :third-party-cost-payment="thirdPartyCostPayment"
    :invoice-items="paymentFormInvoiceItems"
    @refresh-list="refreshAfterPayment"
  />
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import LoadingComponent from '@c/TheLoadingComponent.vue'
import DashboardHeader from '@c/Dashboard/Components/DashboardHeader.vue'
import ThirdPartyCostTable from '@c/Dashboard/Content/ThirdPartyCost/ThirdPartyCostTable.vue'
import DashboardPanelControl from '@c/Dashboard/Components/DashboardPanelControl.vue'
import FilterLinesComponent from '@c/Dashboard/Components/FilterLinesComponent.vue'
import AddNewColumnComponent from '@c/Dashboard/Content/Invoicing/components/Head/columnSettings/AddNewColumnComponent.vue'
import BaseButton from '@c/addf-package/components/BaseButton/BaseButton.vue'
import InvoiceItemEditDialog from '@/views/invoicing/show/tables/dialogs/InvoiceItemEditDialog.vue'
import ThirdPartyCostPaymentFormModal from '@ui/modal/payment/ThirdPartyCostPaymentFormModal.vue'
import AlertPanel from '@ui/AlertPanel.vue'
import persistViewFiltersStateMixin from '@/mixins/persistViewFiltersStateMixin'

export default {
  name: 'ThirdPartyCostContent',
  components: {
    AlertPanel,
    AddNewColumnComponent,
    FilterLinesComponent,
    DashboardPanelControl,
    ThirdPartyCostTable,
    DashboardHeader,
    LoadingComponent,
    BaseButton,
    InvoiceItemEditDialog,
    ThirdPartyCostPaymentFormModal
  },
  mixins: [persistViewFiltersStateMixin],
  persistViewState: ['searchValue', 'localDashboard.filters', 'selectedItems'],
  props: {
    currentBoard: {
      type: Object,
      required: true
    }
  },
  data() {
    return {
      loading: false,
      loadingItems: false,
      searchValue: '',
      itemsList: [],
      limitAlert: null,
      forceUnlimited: false,
      localDashboard: null,
      requireSaveDashboard: false,
      selectedItems: [],
      paymentForm: false,
      thirdPartyCostPayment: null,
      editInvoiceItems: [],
      showEditDialog: false,
      currentItem: null
    }
  },
  computed: {
    ...mapGetters({
      availableFilter: 'metadata/getAvailableFilterThirdPartyCost'
    }),
    validFilter() {
      return (
        this.localDashboard.filters?.filter(
          (filter) => filter.column && filter.values && filter.condition
        ) ?? []
      )
    },
    filtrableColumns() {
      const searchable = this.localDashboard.columns
        .filter((column) => column.search?.searchable === true)
        .map((column) => column.search.value ?? column.field)
      if (searchable.length > 0) return searchable
      return this.localDashboard.columns.map((column) => column.field)
    },
    filteredItems() {
      let tmpList = [...this.itemsList]
      if (this.searchValue.length >= 2) {
        const searchValue = this.$h.normalizeSearch(this.searchValue)
        tmpList = tmpList.filter((model) => {
          return this.filtrableColumns.some((column) => {
            if (['dossier_redirect', 'contact_redirect'].includes(column)) {
              return this.$h
                .normalizeSearch(model?.[column]?.label?.toString())
                .includes(searchValue)
            }
            if (model?.[column]?.value) {
              return this.$h
                .normalizeSearch(model?.[column]?.value.toString())
                .includes(searchValue)
            }
            return this.$h
              .normalizeSearch(model?.[column]?.toString())
              .includes(searchValue)
          })
        })
      }
      if (this.localDashboard.sort?.column_id) {
        let sortRealColumn = this.localDashboard.columns?.find(
          (column) => column.id === this.localDashboard.sort.column_id
        )
        let sortRealColumnValue =
          sortRealColumn?.sort?.value ?? sortRealColumn?.field
        if (sortRealColumnValue) {
          tmpList.sort((a, b) => {
            const direction =
              this.localDashboard.sort.direction === 'desc' ? -1 : 1
            let aValue = a[sortRealColumnValue] ?? ''
            let bValue = b[sortRealColumnValue] ?? ''
            if (sortRealColumn.type === 'currency') {
              aValue = parseFloat(aValue ?? 0)
              bValue = parseFloat(bValue ?? 0)
            }
            let result =
              typeof aValue === 'number'
                ? aValue - bValue
                : aValue.toString().localeCompare(bValue.toString())
            if (result === 0) {
              const referenceField = this.localDashboard.columns[0]?.field
              if (referenceField) {
                const refA = a[referenceField]?.toString() ?? ''
                const refB = b[referenceField]?.toString() ?? ''
                result = refA.localeCompare(refB)
              }
            }
            return result * direction
          })
        }
      }
      return tmpList
    },
    paymentFormInvoiceItems() {
      if (this.editInvoiceItems.length) return this.editInvoiceItems
      return this.itemsList.filter((item) =>
        this.selectedItems.includes(item.id)
      )
    }
  },
  watch: {
    currentBoard(newValue) {
      this.localDashboard = newValue
      this.fetchTemplateDashboardConfig()
    }
  },
  created() {
    this.fetchTemplateDashboardConfig()
  },
  methods: {
    ...mapActions({
      processFetchListThirdPartyCostDashboardAction:
        'dashboard/fetchListThirdPartyCostDashboard',
      processFetchTemplateDashboardByIdAction:
        'dashboard/fetchTemplateDashboardById',
      processThirdPartyCostExportExcelAction:
        'invoicing/thirdPartyCostExportExcel',
      processFetchNewThirdPartyPaymentAction:
        'invoicing/fetchNewThirdPartyPayment',
      processFetchThirdPartyCostInvoiceItemAction:
        'invoicing/fetchThirdPartyCostInvoiceItem',
      processFetchPaidInvoiceItemsByThirdPartyCostIdAction:
        'invoicing/fetchPaidInvoiceItemsByThirdPartyCostId'
    }),
    clearFilters() {
      this.localDashboard.filters = this.$getInitialState(
        'localDashboard.filters'
      )
      this.forceUnlimited = false
      this.fetchItems()
    },
    fetchTemplateDashboardConfig() {
      this.loading = true
      const targetId = this.currentBoard.id
      this.processFetchTemplateDashboardByIdAction({ id: targetId })
        .then((response) => {
          if (this.currentBoard.id !== targetId) return
          this.localDashboard = response.template_dashboard
          this.$restoreCachedState('localDashboard.filters', response.template_dashboard.filters)
          this.fetchItems()
        })
        .catch(() => {
          this.$router.push({ name: 'dashboard_main_page_route' })
        })
        .finally(() => {
          if (this.currentBoard.id === targetId) this.loading = false
        })
    },
    updateDashboard(reloadItems) {
      this.$refs.panel_control.saveDashboard(() => {
        if (reloadItems) {
          this.fetchItems()
        }
      })
    },
    fetchItems() {
      this.itemsList = []
      this.limitAlert = null
      this.loadingItems = true
      this.processFetchListThirdPartyCostDashboardAction({
        filters: this.localDashboard.filters,
        columns: this.localDashboard.columns,
        template_id: this.localDashboard.id,
        force_unlimited: this.forceUnlimited
      })
        .then((response) => {
          this.itemsList = response.invoice_items
          this.limitAlert = response.alert ?? null
          this.restoreSelectionForCurrentItems()
        })
        .catch((error) => {
          if (error.code !== 'ERR_CANCELED') {
            this.$h.toastDanger(this.$t('toasts.something_went_wrong'))
          }
        })
        .finally(() => (this.loadingItems = false))
    },
    overrideLimit() {
      this.forceUnlimited = true
      this.fetchItems()
    },
    closePopover(close) {
      if (typeof close === 'function') {
        close()
      }
      this.fetchItems()
    },
    dashboardChanged() {
      // intentionally empty — no action needed on dashboard change for this dashboard type
    },
    addColumn(config) {
      this.localDashboard.columns.push({
        ...config,
        sum_able: ['currency'].includes(config.type?.toLowerCase()),
        template_id: this.localDashboard.id
      })
    },
    handleExportFile() {
      this.processThirdPartyCostExportExcelAction({
        filters: this.localDashboard.filters,
        columns: this.localDashboard.columns,
        template_id: this.localDashboard.id,
        sort: this.localDashboard.sort,
        terms: this.searchValue
      })
        .then((response) => {
          window.open(response.url)
        })
        .catch(() => {
          this.$h.toastDanger(this.$t('toasts.something_went_wrong'))
        })
    },
    /**
     * The selection is cached per board, so leaving for a dossier and coming back keeps it:
     * that restoration happens once, at mount, via the persistViewFiltersStateMixin. This
     * method only re-validates the current selection on every fetchItems() resolution
     * (filter change, sort, clear filters, force-unlimited, refreshAfterPayment). Only ids
     * still returned and still unpaid may stay selected: a settlement must never carry rows
     * the user can no longer see, and rows paid in the meantime are dropped here.
     */
    restoreSelectionForCurrentItems() {
      const selectableIds = this.itemsList
        .filter((item) => !item.paid)
        .map((item) => item.id)
      this.selectedItems = this.selectedItems.filter((id) =>
        selectableIds.includes(id)
      )
    },
    toggleSelection(item) {
      if (this.selectedItems.includes(item.id)) {
        this.selectedItems = this.selectedItems.filter((id) => id !== item.id)
      } else {
        this.selectedItems.push(item.id)
      }
    },
    printPaymentProof(item) {
      if (item.third_party_cost_payment_id) {
        this.$router.push({
          name: 'third_party_cost_print_payment_route',
          params: {
            third_party_cost_payment_id: item.third_party_cost_payment_id
          }
        })
      }
    },
    openEditDialog(item) {
      this.processFetchThirdPartyCostInvoiceItemAction({
        id: item.id,
        invoice_id: item.invoice_id
      })
        .then((invoiceItem) => {
          this.currentItem = invoiceItem
          this.showEditDialog = true
        })
        .catch(() => {
          this.$h.toastDanger(this.$t('toasts.something_went_wrong'))
        })
    },
    openNewPaymentForm() {
      this.editInvoiceItems = []
      this.processFetchNewThirdPartyPaymentAction()
        .then((response) => {
          this.thirdPartyCostPayment = response.thirdPartyCostPayment
          this.paymentForm = true
        })
        .catch(() => {
          this.$h.toastDanger(this.$t('toasts.something_went_wrong'))
        })
    },
    openEditPaymentForm(thirdPartyCostPayment) {
      this.thirdPartyCostPayment = thirdPartyCostPayment
      this.processFetchPaidInvoiceItemsByThirdPartyCostIdAction({
        thirdPartyCostId: thirdPartyCostPayment.id
      })
        .then((response) => {
          this.editInvoiceItems = response.invoice_items ?? []
          this.paymentForm = true
        })
        .catch(() => {
          this.$h.toastDanger(this.$t('toasts.something_went_wrong'))
        })
    },
    refreshAfterPayment() {
      this.paymentForm = false
      this.selectedItems = []
      this.thirdPartyCostPayment = null
      this.editInvoiceItems = []
      this.fetchItems()
    }
  }
}
</script>
